import { useQuery } from '@tanstack/react-query';
import {
  EMR_FORMS,
  ageInYears,
  emrContextOfEncounter,
  emrFormAppliesToPatient,
  emrProgress,
  resolveEmrForms,
  type EmrCompletionFacts,
  type EmrProgress,
  type QueueEntry,
  type ResolvedEmrForm,
  type ResolvedEmrFormsResponse,
} from '@his/shared';
import { get } from '@/lib/api';
import { tr } from '@/lib/i18n-core';
import type { Encounter } from './types';

export const NEWBORN_DAYS = 90;
const DAY_MS = 86_400_000;

function live(status: string | undefined): boolean {
  return status !== 'cancelled';
}

export function completionFactsOf(encounter: Encounter): EmrCompletionFacts {
  const orders = (encounter.clinicalOrders ?? []).filter((order) => live(order.status));
  const ordersOf = (type: string) => orders.filter((order) => order.type === type).length;
  return {
    chiefComplaint: encounter.chiefComplaint,
    notes: encounter.notes,
    followUpDate: encounter.followUpDate,
    vitalsCount: encounter.vitals.length,
    vitalsNotTakenReason: encounter.vitalsNotTakenReason ?? null,
    diagnosesCount: encounter.diagnoses.length,
    prescriptionsCount: encounter.prescriptions.filter((prescription) => live(prescription.status)).length,
    labOrdersCount: (encounter.labOrders ?? []).filter((order) => order.items.some((item) => live(item.status))).length + ordersOf('lab'),
    radiologyOrdersCount: (encounter.radiologyStudies ?? []).filter((study) => live(study.status)).length + ordersOf('radiology'),
    procedureOrdersCount: ordersOf('procedure'),
    notNeeded: encounter.emrNotNeeded ?? [],
    recordedForms: encounter.recordedForms ?? [],
  };
}

export function requiredFormsApply(encounter: Pick<Encounter, 'context'>): boolean {
  return encounter.context === 'opd';
}

function defaultForms(encounter: Encounter): ResolvedEmrForm[] {
  const patient = { sex: encounter.patient.sex, ageYears: ageInYears(encounter.patient.dob?.slice(0, 10) ?? null, encounter.patient.approximateAgeYears) };
  return resolveEmrForms(EMR_FORMS, [], [], emrContextOfEncounter(encounter.context)).filter((form) => emrFormAppliesToPatient(form.key, patient));
}

export interface EmrFormsState {
  forms: ResolvedEmrForm[];
  departmentId: string | null;
  ready: boolean;
}

function emrFormsQuery(encounterId: string | undefined) {
  return {
    queryKey: ['emr-forms', encounterId],
    queryFn: () => get<ResolvedEmrFormsResponse>(`/encounters/${encounterId}/emr-forms`),
    enabled: Boolean(encounterId),
    staleTime: 5 * 60_000,
  };
}

export function useEncounterDepartment(encounterId: string | undefined): { departmentId: string | null; ready: boolean } {
  const query = useQuery(emrFormsQuery(encounterId));
  return { departmentId: query.data?.departmentId ?? null, ready: !encounterId || query.isSuccess || query.isError };
}

export function useEmrForms(encounter: Encounter | undefined): EmrFormsState {
  const query = useQuery(emrFormsQuery(encounter?.id));
  if (query.data) return { forms: query.data.forms, departmentId: query.data.departmentId, ready: true };
  return { forms: encounter ? defaultForms(encounter) : [], departmentId: null, ready: Boolean(encounter) && !query.isPending };
}

export function progressOf(encounter: Encounter, forms: readonly ResolvedEmrForm[]): EmrProgress {
  if (!requiredFormsApply(encounter)) return { required: 0, done: 0, missing: [] };
  return emrProgress(forms, completionFactsOf(encounter));
}

export function ageInDays(dob: string | null | undefined, now = Date.now()): number | null {
  if (!dob) return null;
  const born = Date.parse(dob.length === 10 ? `${dob}T00:00:00Z` : dob);
  if (Number.isNaN(born)) return null;
  return Math.floor((now - born) / DAY_MS);
}

export interface NewbornFacts {
  patientId: string;
  birthWeightGrams: number | null;
  gestationalAgeWeeks: number | null;
  gestationalAgeDays: number | null;
}

export function birthWeightText(grams: number | null): string | null {
  return grams === null ? null : tr('opd', 'emr_birth_weight_kg', { kg: (grams / 1000).toFixed(2) });
}

export function gestationText(weeks: number | null, days: number | null): string | null {
  if (weeks === null) return null;
  return days ? tr('opd', 'emr_gestation_weeks_days', { weeks, days }) : tr('opd', 'emr_gestation_weeks', { weeks });
}

export function newbornFactsOf(
  patient: Pick<Encounter['patient'], 'id' | 'dob' | 'birthWeightGrams' | 'gestationalAgeWeeks' | 'gestationalAgeDays'>,
  now = Date.now(),
): NewbornFacts | null {
  const days = ageInDays(patient.dob, now);
  if (days === null || days < 0 || days >= NEWBORN_DAYS) return null;
  return {
    patientId: patient.id,
    birthWeightGrams: patient.birthWeightGrams ?? null,
    gestationalAgeWeeks: patient.gestationalAgeWeeks ?? null,
    gestationalAgeDays: patient.gestationalAgeDays ?? null,
  };
}

export function queueNewbornFacts(
  entry: Pick<QueueEntry, 'patientId' | 'dob' | 'birthWeightGrams' | 'gestationalAgeWeeks' | 'gestationalAgeDays'>,
  now = Date.now(),
): NewbornFacts | null {
  return newbornFactsOf(
    {
      id: entry.patientId,
      dob: entry.dob ?? null,
      birthWeightGrams: entry.birthWeightGrams ?? null,
      gestationalAgeWeeks: entry.gestationalAgeWeeks ?? null,
      gestationalAgeDays: entry.gestationalAgeDays ?? null,
    },
    now,
  );
}
